import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Box, Typography, Paper, Autocomplete, TextField, Chip, Stack, Tabs, Tab,
  IconButton, Input
} from '@mui/material';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import TeamEloChart from './TeamEloChart';
import TeamSimulationChart from './TeamSimulationChart'; 
import { CHART_COLORS } from '../constants/ChartConfig';
const TeamAnalytics = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const chartType = searchParams.get('type') || 'teamElo';
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [allTeams, setAllTeams] = useState([]);
  const [nextColorIndex, setNextColorIndex] = useState(0);
  
  const [inputValue, setInputValue] = useState('');
  const [autocompleteKey, setAutocompleteKey] = useState(0);
  
  const [startPlace, setStartPlace] = useState(1);
  const [endPlace, setEndPlace] = useState(1);
  
  const inputRef = useRef(null);
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/teams`)
      .then(response => {
        const teams = response.data.map(team => ({
          ...team,
          label: team.teamName,
          teamId: team.teamId
        }));
        setAllTeams(teams);
        const defaultTeam = teams.find(t => t.teamId === parseInt(id));
        if (defaultTeam) {
          setSelectedTeams([{ ...defaultTeam, color: CHART_COLORS[0] }]);
          setNextColorIndex(1);
        }
      })
      .catch(err => console.error("Błąd ładowania drużyn:", err));
  }, [id]);
  const handleTeamSelect = (event, newValue) => {
    if (!newValue) return;
    if (!selectedTeams.find(t => t.teamId === newValue.teamId)) {
      const teamWithColor = { 
        ...newValue, 
        color: CHART_COLORS[nextColorIndex % CHART_COLORS.length] 
      };
      setSelectedTeams([...selectedTeams, teamWithColor]);
      setNextColorIndex(prev => prev + 1);
    }
    setInputValue('');
    setAutocompleteKey(prev => prev + 1);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 50);
  };
  const removeTeam = (teamId) => setSelectedTeams(selectedTeams.filter(t => t.teamId !== teamId));
  const handleTabChange = (event, newValue) => setSearchParams({ type: newValue });
  const handleRangeChange = (type, value) => {
    const val = Math.max(1, Math.min(18, parseInt(value) || 1));
    if (type === 'start') setStartPlace(val); else setEndPlace(val);
  };
  const handleBlur = () => { if (startPlace > endPlace) setEndPlace(startPlace); };
  return (
    <Box sx={{ p: { xs: 0.75, sm: 1.5, md: 3 }, maxWidth: 1200, mx: 'auto', color: '#fff' }}>
      <Paper sx={{ 
        p: { xs: 1, sm: 1.5, md: 3 }, 
        backgroundColor: '#1e1e1e', 
        color: '#fff', 
        borderRadius: 2,
        overflowX: 'hidden',
        overflowY: 'visible'
      }}>
        <Typography 
          sx={{ 
            mb: { xs: 1, md: 3 }, 
            fontWeight: 'bold', 
            textAlign: 'center',
            fontSize: { xs: '1rem', sm: '1.15rem', md: '1.5rem' },
          }}
        >
          Analityka Drużynowa
        </Typography>
        {/* Autocomplete */}
        <Box sx={{ mb: { xs: 1, md: 4 } }}>
          <Autocomplete
            key={autocompleteKey}
            size="small"
            options={allTeams}
            getOptionLabel={(option) => option.teamName}
            value={null}
            inputValue={inputValue}
            onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
            onChange={handleTeamSelect}
            blurOnSelect
            renderInput={(params) => (
              <TextField 
                {...params} 
                inputRef={inputRef}
                label="Dodaj drużynę" 
                variant="outlined"
                size="small"
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    color: '#fff', 
                    fontSize: { xs: '0.6rem', sm: '0.8rem', md: '0.95rem' },
                    py: { xs: '0px', md: '2px' },
                    '& fieldset': { borderColor: '#444' },
                    '&:hover fieldset': { borderColor: '#666' }
                  },
                  '& .MuiInputLabel-root': { 
                    color: '#aaa',
                    fontSize: { xs: '0.6rem', sm: '0.8rem', md: '0.95rem' },
                  }
                }}
              />
            )}
          />
        </Box>
        {/* Chipy drużyn */}
        <Stack 
          direction="row" 
          spacing={0.5} 
          flexWrap="wrap" 
          useFlexGap 
          sx={{ mb: { xs: 1, md: 3 }, gap: { xs: 0.3, sm: 0.5, md: 1 } }}
        >
          {selectedTeams.map((team) => (
            <Chip
              key={team.teamId}
              label={team.teamName}
              size="small"
              onDelete={() => removeTeam(team.teamId)}
              sx={{ 
                color: team.color, 
                borderColor: team.color, 
                fontWeight: 'bold', 
                backgroundColor: 'rgba(255,255,255,0.05)',
                fontSize: { xs: '0.6rem', sm: '0.7rem', md: '0.85rem' },
                height: { xs: 22, sm: 24, md: 32 },
                '& .MuiChip-label': {
                  px: { xs: 0.7, md: 1.5 },
                },
                '& .MuiChip-deleteIcon': {
                  fontSize: { xs: '0.8rem', md: '1.1rem' },
                },
              }}
              variant="outlined"
            />
          ))}
        </Stack>
        {/* Tabs — zawsze scrollable */}
        <Tabs 
          value={chartType} 
          onChange={handleTabChange} 
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ 
            mb: { xs: 1, md: 3 }, 
            minHeight: { xs: 30, sm: 36, md: 48 },
            '& .MuiTabs-flexContainer': {
			  justifyContent: 'center',
			},
			'& .MuiTab-root': { 
              color: '#aaa',
              fontSize: { xs: '0.65rem', sm: '0.72rem', md: '0.875rem' },
              minHeight: { xs: 30, sm: 36, md: 48 },
              px: { xs: 0.75, sm: 1, md: 2 },
              py: { xs: 0.25, sm: 0.5, md: 1 },
              minWidth: { xs: 'auto', md: 90 },
            }, 
            '& .Mui-selected': { color: '#fff !important' }, 
            '& .MuiTabs-indicator': { backgroundColor: '#4fc3f7' },
            '& .MuiTabs-scrollButtons': { color: '#aaa' },
          }}
        >
          <Tab label="Ranking Elo" value="teamElo" />
          <Tab label="Szanse na miejsca" value="positionChances" />
          <Tab label="xPts" value="expectedPoints" />
          <Tab label="xPos" value="expectedPosition" />
        </Tabs>
        {/* Zakres miejsc */}
        {chartType === 'positionChances' && (
          <Box sx={{ 
            mb: { xs: 1, md: 3 }, 
            display: 'flex', 
            alignItems: 'center', 
            gap: { xs: 0.5, md: 2 }, 
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}>
            <Typography variant="body2" sx={{ color: '#aaa', fontSize: { xs: '0.65rem', sm: '0.70rem', md: '0.875rem' } }}>
              Zakres miejsc:
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              backgroundColor: '#333', 
              borderRadius: 1, 
              px: { xs: 0.4, md: 1 },
              py: { xs: 0.2, md: 0 },
            }}>
              <Typography sx={{ fontSize: { xs: '0.65rem', md: '0.8rem' }, color: '#aaa', mr: 0.5 }}>OD</Typography>
              <IconButton size="small" onClick={() => handleRangeChange('start', startPlace - 1)} sx={{ color: '#fff', p: { xs: 0.25, md: 0.5 } }}><RemoveIcon sx={{ fontSize: { xs: '0.85rem', md: '1.1rem' } }} /></IconButton>
              <Input value={startPlace} onChange={(e) => handleRangeChange('start', e.target.value)} onBlur={handleBlur} sx={{ color: '#fff', fontSize: { xs: '0.75rem', md: '0.9rem' }, width: '22px', '& input': { textAlign: 'center', p: 0 } }} />
              <IconButton size="small" onClick={() => handleRangeChange('start', startPlace + 1)} sx={{ color: '#fff', p: { xs: 0.25, md: 0.5 } }}><AddIcon sx={{ fontSize: { xs: '0.85rem', md: '1.1rem' } }} /></IconButton>
              <Typography sx={{ fontSize: { xs: '0.65rem', md: '0.8rem' }, color: '#aaa', mx: { xs: 0.5, md: 1 } }}>DO</Typography>
              <IconButton size="small" onClick={() => handleRangeChange('end', endPlace - 1)} sx={{ color: '#fff', p: { xs: 0.25, md: 0.5 } }}><RemoveIcon sx={{ fontSize: { xs: '0.85rem', md: '1.1rem' } }} /></IconButton>
              <Input value={endPlace} onChange={(e) => handleRangeChange('end', e.target.value)} onBlur={handleBlur} sx={{ color: '#fff', fontSize: { xs: '0.75rem', md: '0.9rem' }, width: '22px', '& input': { textAlign: 'center', p: 0 } }} />
              <IconButton size="small" onClick={() => handleRangeChange('end', endPlace + 1)} sx={{ color: '#fff', p: { xs: 0.25, md: 0.5 } }}><AddIcon sx={{ fontSize: { xs: '0.85rem', md: '1.1rem' } }} /></IconButton>
            </Box>
          </Box>
        )}
        {/* Wykres — lepiej dopasowana wysokość */}
        <Box sx={{ 
           minHeight: { xs: 300, sm: 360, md: 450 },
          height: { xs: 'clamp(300px, 58dvh, 480px)', sm: 'clamp(360px, 62dvh, 560px)', md: 'calc(100dvh - 300px)' },
          maxHeight: { xs: 'none', sm: 600, md: 700 },
          overflow: 'visible',
        }}>
          {selectedTeams.length === 0 ? (
            <Typography sx={{ p: 4, textAlign: 'center', color: '#666' }}>Wybierz drużynę do analizy</Typography>
          ) : (
            <>
              {chartType === 'teamElo' && <TeamEloChart selectedTeams={selectedTeams} />}
              {chartType === 'positionChances' && <TeamSimulationChart dataType="probability" selectedTeams={selectedTeams} startPlace={startPlace} endPlace={endPlace} />}
              {chartType === 'expectedPoints' && <TeamSimulationChart dataType="avgPoints" selectedTeams={selectedTeams} />}
              {chartType === 'expectedPosition' && <TeamSimulationChart dataType="avgPosition" selectedTeams={selectedTeams} />}
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
};
export default TeamAnalytics;